import { useCallback } from 'react';

import { useLocalStorage } from './useLocalStorage';

/**
 * Single global key — every list table shares ONE zoom level, so the whole app's
 * tables stay the same size (changing it on one page applies everywhere).
 */
const STORAGE_KEY = 'table-zoom';

export const TABLE_ZOOM_MIN = 0.7;
export const TABLE_ZOOM_MAX = 1.5;
export const TABLE_ZOOM_STEP = 0.1;
const DEFAULT_ZOOM = 1;

/** Clamp to the allowed range and snap to one decimal so steps stay clean. */
const clamp = (value: number): number =>
  Math.min(TABLE_ZOOM_MAX, Math.max(TABLE_ZOOM_MIN, Math.round(value * 10) / 10));

export interface TableZoom {
  /** Current zoom factor (1 = 100%). Feed into a `zoom` CSS style. */
  zoom: number;
  zoomIn: () => void;
  zoomOut: () => void;
  reset: () => void;
  canZoomIn: boolean;
  canZoomOut: boolean;
}

/**
 * App-wide table zoom (browser Ctrl +/- style). One shared level for every list
 * table, persisted to localStorage so it survives refresh and syncs across tabs.
 */
export function useTableZoom(): TableZoom {
  const [stored, setZoom] = useLocalStorage<number>(STORAGE_KEY, DEFAULT_ZOOM);
  const zoom = clamp(typeof stored === 'number' ? stored : DEFAULT_ZOOM);

  const zoomIn = useCallback(
    () => setZoom((prev) => clamp((typeof prev === 'number' ? prev : DEFAULT_ZOOM) + TABLE_ZOOM_STEP)),
    [setZoom],
  );
  const zoomOut = useCallback(
    () => setZoom((prev) => clamp((typeof prev === 'number' ? prev : DEFAULT_ZOOM) - TABLE_ZOOM_STEP)),
    [setZoom],
  );
  const reset = useCallback(() => setZoom(DEFAULT_ZOOM), [setZoom]);

  return {
    zoom,
    zoomIn,
    zoomOut,
    reset,
    canZoomIn: zoom < TABLE_ZOOM_MAX,
    canZoomOut: zoom > TABLE_ZOOM_MIN,
  };
}
